import { useState, useEffect, useCallback } from 'react'
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt, useChainId, useSwitchChain, usePublicClient } from 'wagmi'
import { parseAbi, parseEther, formatEther, keccak256, toBytes } from 'viem'
import { useToast } from '../context/ToastContext'
import { Icon } from '../components/Layout'
import { uploadBannerToPinata } from '../services/pinata'

const MARKET_ADDRESS = import.meta.env.VITE_AD_MARKETPLACE_ADDRESS
const CLT_ADDRESS    = import.meta.env.VITE_CLT_CONTRACT_ADDRESS
const SEPOLIA_ID     = 11155111

const MARKET_ABI = parseAbi([
  'struct Ad { address advertiser; bytes32 placementId; uint256 durationDays; string metadataURI; uint8 payToken; uint256 amountPaid; uint8 status; bool refunded; bool withdrawn; }',
  'struct Placement { uint256 ethPrice; uint256 cltPrice; bool exists; }',
  'function submitAdWithEth(bytes32 placementId, uint256 durationDays, string calldata metadataURI) external payable returns (uint256)',
  'function submitAdWithClt(bytes32 placementId, uint256 durationDays, string calldata metadataURI) external returns (uint256)',
  'function refund(uint256 adId) external',
  'function getAd(uint256 adId) external view returns (Ad)',
  'function getPlacement(bytes32 placementId) external view returns (Placement)',
  'function placementIdFromString(string calldata name) external pure returns (bytes32)',
  'function nextAdId() external view returns (uint256)',
])

const CLT_ABI = parseAbi([
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function balanceOf(address owner) external view returns (uint256)',
])

const PLACEMENTS = [
  { name: 'feed',        label: 'Main Feed',         badge: 'Recommended' },
  { name: 'banner',      label: 'Marketplace Banner', badge: null },
]

const DURATION_OPTIONS = [7, 14, 30, 60, 90]
const STATUS_LABEL = ['Pending', 'Approved', 'Rejected']
const STATUS_COLOR = [
  'text-yellow-500 bg-yellow-500/10 border-yellow-500/30',
  'text-green-500 bg-green-500/10 border-green-500/30',
  'text-error bg-error/10 border-error/30',
]
const PAY_TOKEN = { ETH: 0, CLT: 1 }

function placementId(name) {
  return keccak256(toBytes(name))
}

function fmt(wei) {
  if (!wei) return '0'
  return parseFloat(formatEther(wei)).toFixed(4).replace(/\.?0+$/, '')
}

function fmtClt(wei) {
  if (!wei) return '0'
  const n = Number(formatEther(wei))
  return n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n.toFixed(2)
}

function NotDeployedBanner() {
  return (
    <div className="border-4 border-dashed border-on-background/20 p-10 text-center flex flex-col gap-3 items-center">
      <span className="material-symbols-outlined text-[40px] opacity-30">construction</span>
      <p className="font-extrabold uppercase tracking-widest text-[13px]">Marketplace contract not connected</p>
      <p className="text-[12px] text-on-surface-variant">Set VITE_AD_MARKETPLACE_ADDRESS in your env and redeploy.</p>
    </div>
  )
}

function WrongNetworkBanner() {
  const { switchChain, isPending } = useSwitchChain()
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/70 p-4">
      <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6 text-center"
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
        <span className="material-symbols-outlined text-[40px] text-error mx-auto">wrong_location</span>
        <div>
          <p className="font-extrabold text-[15px] uppercase tracking-widest">Wrong Network</p>
          <p className="text-[13px] text-on-surface-variant mt-1">Chicago Marketplace runs on <strong>Ethereum Sepolia</strong>.</p>
        </div>
        <button onClick={() => switchChain({ chainId: SEPOLIA_ID })} disabled={isPending}
          className="py-3 bg-on-background text-surface font-bold text-[12px] uppercase tracking-widest hover:opacity-90 disabled:opacity-50 transition-opacity border-2 border-on-background"
          style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}>
          {isPending ? 'Switching…' : 'Switch to Sepolia'}
        </button>
      </div>
    </div>
  )
}

function PlacementCard({ placement, selected, onSelect }) {
  const pid = placementId(placement.name)
  const { data: p } = useReadContract({
    address: MARKET_ADDRESS,
    abi: MARKET_ABI,
    functionName: 'getPlacement',
    args: [pid],
    query: { enabled: !!MARKET_ADDRESS },
  })

  return (
    <button onClick={() => onSelect(placement.name)}
      className={`flex items-center justify-between p-4 border-2 transition-colors text-left w-full
        ${selected ? 'border-on-background bg-surface-container' : 'border-on-background/20 hover:border-on-background/50'}`}>
      <div className="flex items-center gap-3">
        <div className={`w-4 h-4 rounded-full border-2 border-on-background flex items-center justify-center ${selected ? 'bg-on-background' : ''}`}>
          {selected && <div className="w-2 h-2 rounded-full bg-surface" />}
        </div>
        <div>
          <p className="font-bold text-[14px]">{placement.label}</p>
          <p className="text-[11px] text-on-surface-variant mt-0.5">
            {p ? `${fmt(p.ethPrice)} ETH · ${fmtClt(p.cltPrice)} CLT` : 'Loading prices…'}
          </p>
        </div>
      </div>
      {placement.badge && (
        <span className="font-bold bg-primary-container text-on-primary-fixed px-2 py-0.5 text-[9px] uppercase shrink-0">
          {placement.badge}
        </span>
      )}
    </button>
  )
}

function SubmitAdForm({ onSuccess }) {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { switchChainAsync } = useSwitchChain()
  const toast = useToast()
  const { writeContractAsync } = useWriteContract()
  const publicClient = usePublicClient()

  const [selectedPlacement, setSelectedPlacement] = useState('feed')
  const [durationDays, setDurationDays]           = useState(30)
  const [payWith, setPayWith]                     = useState('ETH')
  const [form, setForm]   = useState({ title: '', url: '', description: '' })
  const [bannerFile, setBannerFile]   = useState(null)
  const [bannerPreview, setBannerPreview] = useState(null)
  const [submitting, setSubmitting]   = useState(false)
  const [approving, setApproving]     = useState(false)

  const pid = placementId(selectedPlacement)

  const { data: placement } = useReadContract({
    address: MARKET_ADDRESS,
    abi: MARKET_ABI,
    functionName: 'getPlacement',
    args: [pid],
    query: { enabled: !!MARKET_ADDRESS },
  })

  const { data: nextAdId } = useReadContract({
    address: MARKET_ADDRESS,
    abi: MARKET_ABI,
    functionName: 'nextAdId',
    query: { enabled: !!MARKET_ADDRESS },
  })

  const price = payWith === 'ETH' ? placement?.ethPrice : placement?.cltPrice

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: CLT_ADDRESS,
    abi: CLT_ABI,
    functionName: 'allowance',
    args: [address, MARKET_ADDRESS],
    query: { enabled: !!address && !!MARKET_ADDRESS && payWith === 'CLT' },
  })

  const needsApproval = payWith === 'CLT' && price && (!allowance || allowance < price)

  const [bannerUrl, setBannerUrl] = useState(null)
  const [bannerProcessing, setBannerProcessing] = useState(false)
  const [bannerError, setBannerError] = useState(null)

  // Banners are uploaded to IPFS (via Pinata) and only the resulting gateway
  // URL is stored in the on-chain metadataURI — embedding the image itself
  // (even compressed/base64) made submitAdWithClt/Eth revert on Sepolia: a
  // ~60KB string costs ~40M+ gas to persist on-chain, well past the
  // ~16.7M gas cap. We still downscale/compress client-side first, purely
  // to keep the upload fast and Pinata storage small — not for gas reasons
  // anymore, since the image never touches the chain.
  const MAX_BANNER_DIM = 1280
  const BANNER_JPEG_QUALITY = 0.8

  const compressBanner = (file) => new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      const scale = Math.min(1, MAX_BANNER_DIM / Math.max(img.width, img.height))
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, w, h)
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Could not compress image')); return }
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }))
      }, 'image/jpeg', BANNER_JPEG_QUALITY)
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Could not read image'))
    }
    img.src = objectUrl
  })

  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setBannerFile(file)
    setBannerPreview(URL.createObjectURL(file))
    setBannerError(null)
    setBannerProcessing(true)
    try {
      const compressedFile = await compressBanner(file)
      const url = await uploadBannerToPinata(compressedFile)
      setBannerUrl(url)
    } catch (e) {
      const msg = e.message ?? 'Could not upload that image. Try a different file.'
      setBannerError(msg)
      toast.error(msg)
      setBannerUrl(null)
    } finally {
      setBannerProcessing(false)
    }
  }

  const removeBanner = () => {
    if (bannerPreview) URL.revokeObjectURL(bannerPreview)
    setBannerFile(null)
    setBannerPreview(null)
    setBannerUrl(null)
    setBannerError(null)
  }

  const buildMetadataUri = () => {
    const meta = {
      title: form.title,
      url: form.url,
      description: form.description,
      placement: selectedPlacement,
      ...(bannerUrl ? { banner: bannerUrl } : {}),
    }
    return `data:application/json;base64,${btoa(JSON.stringify(meta))}`
  }

  const ensureSepolia = async () => {
    if (chainId === SEPOLIA_ID) return true
    try {
      await switchChainAsync({ chainId: SEPOLIA_ID })
      return true
    } catch {
      toast.error('Switch your wallet to Ethereum Sepolia first.')
      return false
    }
  }

  const handleApprove = async () => {
    if (!await ensureSepolia()) return
    setApproving(true)
    try {
      const hash = await writeContractAsync({
        address: CLT_ADDRESS,
        abi: CLT_ABI,
        functionName: 'approve',
        args: [MARKET_ADDRESS, price],
      })
      if (publicClient) {
        await publicClient.waitForTransactionReceipt({ hash })
      }
      await refetchAllowance()
      toast.success('CLT approved! Now submit your ad.')
    } catch (e) {
      toast.error(e.shortMessage ?? e.message ?? 'Approval failed')
    } finally {
      setApproving(false)
    }
  }

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Campaign name is required'); return }
    if (!form.url.trim()) { toast.error('Destination URL is required'); return }
    if (!await ensureSepolia()) return
    setSubmitting(true)
    try {
      const metadataURI = buildMetadataUri()
      let hash
      if (payWith === 'ETH') {
        hash = await writeContractAsync({
          address: MARKET_ADDRESS,
          abi: MARKET_ABI,
          functionName: 'submitAdWithEth',
          args: [pid, BigInt(durationDays), metadataURI],
          value: price,
        })
      } else {
        hash = await writeContractAsync({
          address: MARKET_ADDRESS,
          abi: MARKET_ABI,
          functionName: 'submitAdWithClt',
          args: [pid, BigInt(durationDays), metadataURI],
        })
      }
      if (publicClient) await publicClient.waitForTransactionReceipt({ hash })
      toast.success('Ad submitted! Pending admin approval.')

      const optimisticAd = nextAdId != null ? {
        id: Number(nextAdId),
        advertiser: address,
        placementId: pid,
        durationDays: BigInt(durationDays),
        metadataURI,
        payToken: PAY_TOKEN[payWith],
        amountPaid: price,
        status: 0,
        refunded: false,
        withdrawn: false,
      } : null

      setForm({ title: '', url: '', description: '' })
      removeBanner()
      setSubmitting(false)
      onSuccess?.(optimisticAd)
      return
    } catch (e) {
      toast.error(e.shortMessage ?? e.message ?? 'Submission failed')
    }
    setSubmitting(false)
  }

  if (!isConnected) {
    return (
      <div className="border-4 border-dashed border-on-background/20 p-10 text-center">
        <p className="font-bold text-[13px] uppercase tracking-widest text-on-surface-variant">Connect your wallet to place an ad</p>
      </div>
    )
  }

  return (
    <div className="bg-surface border-4 border-on-background flex flex-col gap-6 p-6"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      <h3 className="font-extrabold uppercase text-[15px] tracking-widest">Create Ad Campaign</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Campaign Name <span className="text-error">*</span></label>
          <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            placeholder="Enter campaign name"
            className="border-2 border-on-background bg-surface p-2.5 text-[13px] focus:outline-none focus:border-primary-container" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Destination URL <span className="text-error">*</span></label>
          <input value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
            placeholder="https://your-project.xyz" type="url"
            className="border-2 border-on-background bg-surface p-2.5 text-[13px] focus:outline-none focus:border-primary-container" />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Description</label>
        <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
          rows={3} placeholder="Describe your project or campaign…"
          className="border-2 border-on-background bg-surface p-2.5 text-[13px] focus:outline-none focus:border-primary-container resize-none" />
      </div>

      <div className="flex flex-col gap-1">
        <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
          Project Banner <span className="opacity-40 normal-case font-normal">— recommended 1200×628px</span>
        </label>
        {bannerPreview ? (
          <div className="relative group">
            <img src={bannerPreview} alt="Banner preview" className="w-full h-40 object-cover border-2 border-on-background" />
            {bannerProcessing ? (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <p className="text-white font-bold text-xs uppercase tracking-widest">Uploading…</p>
              </div>
            ) : (
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                <label className="cursor-pointer px-3 py-1.5 bg-surface text-on-surface font-bold text-xs uppercase border-2 border-on-background">
                  Change<input type="file" accept="image/*" className="hidden" onChange={handleBannerChange} />
                </label>
                <button onClick={removeBanner} className="px-3 py-1.5 bg-error text-on-error font-bold text-xs uppercase border-2 border-on-background">Remove</button>
              </div>
            )}
            {!bannerProcessing && !bannerUrl && (
              <p className="text-[10px] text-error font-bold mt-1">{bannerError || "Banner won't be attached — upload failed."}</p>
            )}
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center h-36 border-2 border-dashed border-on-background/30 hover:border-primary-container cursor-pointer transition-colors group">
            <span className="material-symbols-outlined text-[36px] opacity-30 group-hover:opacity-60 transition-opacity">add_photo_alternate</span>
            <p className="font-bold text-xs uppercase tracking-widest opacity-40 mt-2">Upload Banner</p>
            <p className="text-[10px] opacity-30 mt-1">PNG, JPG or GIF — max 5MB</p>
            <input type="file" accept="image/*" className="hidden" onChange={handleBannerChange} />
          </label>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Placement</label>
        <div className="flex flex-col -space-y-[2px]">
          {PLACEMENTS.map(p => (
            <PlacementCard key={p.name} placement={p} selected={selectedPlacement === p.name} onSelect={setSelectedPlacement} />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Duration</label>
        <div className="flex gap-2 flex-wrap">
          {DURATION_OPTIONS.map(d => (
            <button key={d} onClick={() => setDurationDays(d)}
              className={`px-4 py-2 border-2 font-bold text-[11px] uppercase tracking-widest transition-colors
                ${durationDays === d ? 'bg-on-background text-surface border-on-background' : 'border-on-background/30 hover:border-on-background/60'}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Pay With</label>
        <div className="flex border-2 border-on-background">
          {['ETH', 'CLT'].map(token => (
            <button key={token} onClick={() => setPayWith(token)}
              className={`flex-1 py-2.5 font-bold text-[11px] uppercase tracking-widest transition-colors
                ${payWith === token ? 'bg-on-background text-surface' : 'hover:bg-surface-container'}`}>
              {token}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between bg-on-background text-surface p-5">
        <div>
          <p className="font-bold uppercase text-[10px] opacity-60 tracking-widest">Total Cost</p>
          <p className="font-extrabold text-2xl">
            {price ? (payWith === 'ETH' ? `${fmt(price)} ETH` : `${fmtClt(price)} CLT`) : '—'}
          </p>
          <p className="text-[10px] opacity-50 mt-0.5">{durationDays} days · {PLACEMENTS.find(p => p.name === selectedPlacement)?.label}</p>
        </div>
        {needsApproval ? (
          <button onClick={handleApprove} disabled={approving}
            className="bg-primary-container text-on-primary-fixed px-6 py-3 font-extrabold uppercase text-[12px] border-2 border-primary-container hover:opacity-90 disabled:opacity-50 transition">
            {approving ? 'Approving…' : 'Approve CLT'}
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting || !price || bannerProcessing}
            className="bg-primary-container text-on-primary-fixed px-6 py-3 font-extrabold uppercase text-[12px] border-2 border-primary-container hover:opacity-90 disabled:opacity-50 transition">
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </button>
        )}
      </div>
    </div>
  )
}

function MyAdsTab({ optimisticAd, onConsumeOptimistic }) {
  const { address } = useAccount()
  const chainId = useChainId()
  const { switchChainAsync } = useSwitchChain()
  const { writeContractAsync } = useWriteContract()
  const toast = useToast()
  const publicClient = usePublicClient()
  const [ads, setAds] = useState(optimisticAd ? [optimisticAd] : [])
  const [loading, setLoading] = useState(!optimisticAd)
  const [refunding, setRefunding] = useState(null)

  const { data: nextAdId, refetch: refetchNextAdId } = useReadContract({
    address: MARKET_ADDRESS,
    abi: MARKET_ABI,
    functionName: 'nextAdId',
    query: { enabled: !!MARKET_ADDRESS },
  })

  const fetchAds = useCallback(async () => {
    if (!address || !MARKET_ADDRESS || !publicClient) return
    setLoading(true)
    try {
      // Make sure we have the latest count — the cached value can be stale
      // right after a fresh submission, which is what made new ads appear missing.
      const { data: freshNextAdId } = await refetchNextAdId()
      const total = Number(freshNextAdId ?? nextAdId ?? 0)
      const results = []
      for (let i = 0; i < total; i++) {
        try {
          const ad = await publicClient.readContract({
            address: MARKET_ADDRESS,
            abi: MARKET_ABI,
            functionName: 'getAd',
            args: [BigInt(i)],
          })
          if (ad.advertiser?.toLowerCase() === address.toLowerCase()) {
            results.push({ id: i, ...ad })
          }
        } catch {}
      }
      setAds(results)
      onConsumeOptimistic?.()
    } finally {
      setLoading(false)
    }
  }, [address, nextAdId, publicClient, refetchNextAdId, onConsumeOptimistic])

  useEffect(() => { fetchAds() }, [fetchAds])

  const handleRefund = async (adId) => {
    if (chainId !== SEPOLIA_ID) {
      try { await switchChainAsync({ chainId: SEPOLIA_ID }) } catch {
        toast.error('Switch your wallet to Ethereum Sepolia first.')
        return
      }
    }
    setRefunding(adId)
    try {
      const hash = await writeContractAsync({
        address: MARKET_ADDRESS,
        abi: MARKET_ABI,
        functionName: 'refund',
        args: [BigInt(adId)],
      })
      if (publicClient) await publicClient.waitForTransactionReceipt({ hash })
      toast.success('Refund processed!')
      fetchAds()
    } catch (e) {
      toast.error(e.shortMessage ?? e.message ?? 'Refund failed')
    } finally {
      setRefunding(null)
    }
  }

  if (!address) {
    return (
      <div className="border-4 border-dashed border-on-background/20 p-10 text-center">
        <p className="font-bold text-[13px] uppercase tracking-widest text-on-surface-variant">Connect your wallet to view your ads</p>
      </div>
    )
  }

  if (loading && ads.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {[1, 2, 3].map(i => <div key={i} className="h-24 bg-surface-container animate-pulse border-2 border-on-background/10" />)}
      </div>
    )
  }

  if (ads.length === 0) {
    return (
      <div className="border-4 border-dashed border-on-background/20 p-10 text-center">
        <p className="font-bold text-[13px] uppercase tracking-widest text-on-surface-variant">No ads submitted yet</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {ads.map(ad => {
        let meta = {}
        try { meta = JSON.parse(atob(ad.metadataURI?.split(',')[1] ?? '')) } catch {}
        const isRejected = ad.status === 2
        const canRefund = isRejected && !ad.refunded
        return (
          <div key={ad.id} className="bg-surface border-4 border-on-background p-5 flex flex-col gap-3"
            style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
            {meta.banner && (
              <img src={meta.banner} alt="" className="w-full h-32 object-cover border-2 border-on-background" />
            )}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-extrabold text-[14px] truncate">{meta.title || `Ad #${ad.id}`}</p>
                <p className="text-[11px] text-on-surface-variant font-mono truncate mt-0.5">{meta.url}</p>
              </div>
              <span className={`shrink-0 px-2 py-1 border font-bold text-[10px] uppercase tracking-widest ${STATUS_COLOR[ad.status]}`}>
                {STATUS_LABEL[ad.status]}
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-on-surface-variant">
              <span>{ad.durationDays}d · {PLACEMENTS.find(p => placementId(p.name) === ad.placementId)?.label ?? 'Unknown placement'}</span>
              <span className="font-mono font-bold">
                {ad.payToken === 0 ? `${fmt(ad.amountPaid)} ETH` : `${fmtClt(ad.amountPaid)} CLT`}
              </span>
            </div>
            {canRefund && (
              <button onClick={() => handleRefund(ad.id)} disabled={refunding === ad.id}
                className="self-start px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors disabled:opacity-50">
                {refunding === ad.id ? 'Refunding…' : 'Claim Refund'}
              </button>
            )}
            {isRejected && ad.refunded && (
              <p className="text-[11px] text-on-surface-variant">✓ Refunded</p>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function Marketplace() {
  const { isConnected } = useAccount()
  const chainId = useChainId()
  const [tab, setTab] = useState('browse')
  const [optimisticAd, setOptimisticAd] = useState(null)
  const isWrongNetwork = isConnected && chainId !== SEPOLIA_ID

  const handleSubmitSuccess = (ad) => {
    setOptimisticAd(ad)
    setTab('myads')
  }

  return (
    <div className="flex-1 lg:ml-[300px] w-full flex flex-col gap-6 pb-24 md:pb-8 px-4 lg:px-0 pt-4 lg:pt-8">

      {isWrongNetwork && <WrongNetworkBanner />}

      <section>
        <h2 className="font-extrabold uppercase leading-none text-[2rem] lg:text-[64px] lg:leading-[72px] lg:tracking-[-0.04em] mb-2">
          Ads
        </h2>
        <p className="text-sm lg:text-lg text-on-surface-variant max-w-2xl">
          Advertise your project to the Chicago community. Pay with ETH or CLT.
        </p>
      </section>

      {!MARKET_ADDRESS ? <NotDeployedBanner /> : (
        <>
          <div className="flex gap-1 border-2 border-on-background self-start">
            {[
              { id: 'browse', label: 'Place an Ad' },
              { id: 'myads',  label: 'My Ads' },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-5 py-2.5 font-bold uppercase text-[11px] tracking-widest transition-colors
                  ${tab === t.id ? 'bg-on-background text-surface' : 'hover:bg-surface-container'}`}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'browse' && <SubmitAdForm onSuccess={handleSubmitSuccess} />}
          {tab === 'myads'  && (
            <MyAdsTab optimisticAd={optimisticAd} onConsumeOptimistic={() => setOptimisticAd(null)} />
          )}
        </>
      )}
    </div>
  )
}
